import { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { useWebinars } from "../hooks/useWebinars"
import { useAuth } from "../hooks/useAuth"
import WebinarList from "../components/webinar/WebinarList"
import { Button } from "../components/ui/button"
import { useTranslation } from "../i18n"
import { TranslationContext } from "../i18n/TranslationContext"
import { Calendar, Video, ExternalLink } from "lucide-react"

export default function MyWebinarsPage() {
  const { tObj } = useTranslation()
  const translationContext = useContext(TranslationContext)
  const userLanguage = translationContext?.language || 'fr'
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { getMyRegistrations, cancelRegistration, loading } = useWebinars()
  const [activeTab, setActiveTab] = useState("UPCOMING")
  const [upcomingWebinars, setUpcomingWebinars] = useState([])
  const [completedWebinars, setCompletedWebinars] = useState([])
  const [loadingUpcoming, setLoadingUpcoming] = useState(false)
  const [loadingCompleted, setLoadingCompleted] = useState(false)
  const [cancellingWebinarId, setCancellingWebinarId] = useState(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login")
      return
    }
    loadMyWebinars()
  }, [isAuthenticated, userLanguage])

  const loadMyWebinars = async () => {
    setLoadingUpcoming(true)
    setLoadingCompleted(true)
    try {
      const [upcoming, completed] = await Promise.all([
        getMyRegistrations({ status: "UPCOMING", limit: 50, language: userLanguage }),
        getMyRegistrations({ status: "COMPLETED", limit: 50, language: userLanguage })
      ])
      setUpcomingWebinars(upcoming?.data || [])
      setCompletedWebinars(completed?.data || [])
    } catch (error) {
      console.error("Erreur lors du chargement des webinaires:", error)
    } finally {
      setLoadingUpcoming(false)
      setLoadingCompleted(false)
    }
  }

  const handleCancelRegistration = async (webinarId) => {
    setCancellingWebinarId(webinarId)
    try {
      await cancelRegistration(webinarId)
      loadMyWebinars() // Recharger
    } catch (error) {
      // L'erreur est déjà gérée dans le hook
    } finally {
      setCancellingWebinarId(null)
    }
  }

  if (!isAuthenticated) {
    return null
  }

  const currentWebinars = activeTab === "UPCOMING" ? upcomingWebinars : completedWebinars
  const isLoading = activeTab === "UPCOMING" ? loadingUpcoming : loadingCompleted

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header amélioré avec style Courses */}
      <div className="relative pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-28 lg:pb-20 overflow-hidden">
        {/* Effets de fond animés */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#203a8a]/10 via-blue-50/50 to-purple-50/30"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-float delay-500"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-3 sm:px-4 md:px-6">
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent animate-gradient">
              {tObj.webinars?.myWebinars?.title?.() || "Mes Webinaires"}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {tObj.webinars?.myWebinars?.subtitle?.() || "Gérez vos inscriptions aux webinaires"}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 md:px-6 pb-6 sm:pb-8">
        {/* Tabs améliorés */}
        <div className="mb-6 sm:mb-8 flex gap-2 sm:gap-3 border-b-2 border-gray-200 overflow-x-auto bg-white/80 backdrop-blur-sm rounded-t-xl p-2">
          <button
            onClick={() => setActiveTab("UPCOMING")}
            className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap rounded-lg ${
              activeTab === "UPCOMING"
                ? "bg-gradient-to-r from-[#203a8a] to-[#2563eb] text-white shadow-lg scale-105"
                : "text-gray-600 hover:text-[#203a8a] hover:bg-gray-100"
            }`}
          >
            {tObj.webinars?.myWebinars?.upcoming?.() || "À venir"} ({upcomingWebinars.length})
          </button>
          <button
            onClick={() => setActiveTab("COMPLETED")}
            className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap rounded-lg ${
              activeTab === "COMPLETED"
                ? "bg-gradient-to-r from-[#203a8a] to-[#2563eb] text-white shadow-lg scale-105"
                : "text-gray-600 hover:text-[#203a8a] hover:bg-gray-100"
            }`}
          >
            {tObj.webinars?.myWebinars?.completed?.() || "Replays"} ({completedWebinars.length})
          </button>
        </div>

        {/* Liste des webinaires */}
        {isLoading && currentWebinars.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{tObj.common?.loading?.() || "Chargement..."}</p>
          </div>
        ) : currentWebinars.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">
              {activeTab === "UPCOMING"
                ? tObj.webinars?.myWebinars?.noUpcoming?.() || "Aucun webinaire à venir"
                : tObj.webinars?.myWebinars?.noCompleted?.() || "Aucun replay disponible"}
            </p>
            {activeTab === "UPCOMING" && (
              <Button
                onClick={() => navigate("/webinars")}
                className="bg-[#203a8a] text-white hover:bg-[#1a1f4a]"
              >
                {tObj.webinars?.myWebinars?.browseWebinars?.() || "Découvrir les webinaires"}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Webinaires à venir */}
            {activeTab === "UPCOMING" && (
              <div>
                <WebinarList
                  webinars={upcomingWebinars}
                  loading={loadingUpcoming}
                  onCancelRegistration={handleCancelRegistration}
                  cancellingWebinarId={cancellingWebinarId}
                />
                {/* Instructions pour les webinaires à venir */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    {tObj.webinars?.myWebinars?.instructions?.title?.() || "Instructions"}
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm">
                    <li>
                      {tObj.webinars?.myWebinars?.instructions?.join?.() || "Cliquez sur 'Rejoindre la réunion' pour accéder au webinaire"}
                    </li>
                    <li>
                      {tObj.webinars?.myWebinars?.instructions?.cancel?.() || "Vous pouvez annuler votre inscription à tout moment"}
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Replays */}
            {activeTab === "COMPLETED" && (
              <div>
                <WebinarList
                  webinars={completedWebinars}
                  loading={loadingCompleted}
                />
                {/* Instructions pour les replays */}
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-2">
                    {tObj.webinars?.myWebinars?.replays?.title?.() || "Replays disponibles"}
                  </h3>
                  <p className="text-green-800 text-sm">
                    {tObj.webinars?.myWebinars?.replays?.description?.() || "Vous pouvez revoir les webinaires auxquels vous étiez inscrit"}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

